import { useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Button } from './Button.js';
import { Input } from './Input.js';
import { Modal } from './Modal.js';
import type { ScreenPrompt } from './ScreenLayout.js';

/**
 * LA FENETRE A CHAMP DU CHASSIS : une question qui attend un MOT, pas un oui.
 *
 * ELLE EST A `ConfirmDialog` CE QU'UNE SAISIE EST A UNE CONFIRMATION. Le
 * chassis ouvre l'une quand une `ScreenAction` porte un `confirm`, l'autre
 * quand elle porte un `prompt` : dans les deux cas l'appelant ne monte aucune
 * fenetre, donc il ne peut pas en oublier une, et les deux se ressemblent parce
 * qu'elles sont le meme moment du meme geste.
 *
 * UN SEUL GESTE L'UTILISE AUJOURD'HUI, « Enregistrer comme modele », et c'est
 * pour ca qu'elle porte ce nom. Elle ne connait pourtant rien aux modeles : elle
 * recoit un `ScreenPrompt` et rend ce qu'il decrit. Un second geste a saisie
 * unique la reprendra sans une ligne de plus.
 *
 * L'ECHEC NE FERME PAS LA FENETRE, et c'est le point le plus important ici. Le
 * nom vient d'etre tape : le perdre pour rejouer le geste ferait retaper la
 * seule chose que la fenetre demandait. On garde donc la saisie, on affiche la
 * phrase de `errorText`, et le meme bouton rejoue. C'est aussi la raison pour
 * laquelle le moteur laisse remonter son exception au lieu d'allumer un
 * "Reessayer" dans la rangee 3.
 */
export interface TemplateNameDialogProps {
  /** Ouverte. Basculer suffit : le Modal anime l'entree ET la sortie. */
  open: boolean;
  /** Ce que la fenetre demande, et ce qu'elle fait de la reponse. */
  prompt: ScreenPrompt;
  /** Sortie : bouton Annuler, voile, Echap, ou saisie acceptee. */
  onClose: () => void;
}

export function TemplateNameDialog({
  open,
  prompt,
  onClose,
}: TemplateNameDialogProps): JSX.Element {
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(prompt.defaultValue ?? '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * LA FENETRE REPART NEUVE A CHAQUE OUVERTURE. Elle reste MONTEE pendant le
   * fondu de fermeture (norme des overlays du socle) : sans cette remise a
   * zero, la deuxieme ouverture rendrait le nom de la premiere, et un echec
   * ancien resterait affiche sous un champ tout neuf.
   */
  useEffect(() => {
    if (!open) return;
    setValue(prompt.defaultValue ?? '');
    setBusy(false);
    setFailed(false);
  }, [open, prompt.defaultValue]);

  /*
   * LE FOCUS VA AU CHAMP, et il faut le prendre APRES le Modal : celui-ci
   * focalise son PANNEAU a l'ouverture (pour qu'Echap reponde tout de suite) et
   * les effets d'un enfant passent AVANT ceux du parent. Une frame plus tard,
   * le panneau a pris puis rendu la main. Meme mecanique que `ConfirmDialog`,
   * pour l'autre element : ici la main est deja sur le clavier, le focus va
   * donc a la SAISIE et non a la sortie.
   */
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => fieldRef.current?.select());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // UN NOM VIDE N'EST PAS UN NOM : le bouton reste eteint tant qu'il n'y a rien
  // a lire. Les espaces seuls ne comptent pas, ici comme a l'enregistrement.
  const nom = value.trim();
  const pret = nom !== '' && !busy;

  const envoyer = (event: FormEvent): void => {
    event.preventDefault();
    if (!pret) return;
    setBusy(true);
    setFailed(false);
    void (async (): Promise<void> => {
      try {
        await prompt.onSubmit(nom);
      } catch {
        /* LA RAISON TECHNIQUE EST DEJA JOURNALISEE PAR L'APPELANT (le moteur
           trace l'echec avec l'id de la fiche). Ici on ne garde que ce qui se
           lit : la fenetre reste ouverte, la saisie reste en place. */
        setBusy(false);
        setFailed(true);
        return;
      }
      setBusy(false);
      onClose();
    })();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={prompt.title}
      size="sm"
      footer={
        /* MEME ORDRE QUE LES FENETRES DE CONFIRMATION : la sortie a gauche, le
           geste a droite. Le focus, lui, n'est pas au meme endroit : rien n'est
           definitif ici, et la touche Entree doit ENREGISTRER ce qu'on vient de
           taper, pas annuler. */
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {prompt.cancelLabel ?? 'Annuler'}
          </Button>
          <Button variant="primary" onClick={envoyer} disabled={!pret}>
            {prompt.confirmLabel}
          </Button>
        </>
      }
    >
      {/* UN VRAI `form` : la touche Entree dans le champ vaut le bouton, sans
          gestionnaire de touche a ecrire. */}
      <form onSubmit={envoyer} className="flex flex-col gap-3">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium leading-[1.3] text-ink-700">
            {prompt.fieldLabel}
          </span>
          <Input
            ref={fieldRef}
            value={value}
            disabled={busy}
            {...(prompt.placeholder != null ? { placeholder: prompt.placeholder } : {})}
            onChange={(event) => setValue(event.target.value)}
          />
        </label>
        {failed && (
          <p role="alert" className="text-sm leading-normal text-danger">
            {prompt.errorText ?? "Ca n'a pas pu etre enregistre. Reessaie."}
          </p>
        )}
      </form>
    </Modal>
  );
}

export default TemplateNameDialog;
